import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { FiscalYearPicker } from '@/components/sales-trackers/FiscalYearPicker'
import { TargetsDialog } from '@/components/sales-trackers/TargetsDialog'
import { spreadAcrossMonths } from '@/components/sales-trackers/targets-math'
import {
  useBillingsFiscalYears,
  useBillingsRepRoster,
  useBillingsTargets,
  useSaveBillingsTargets,
} from '@/hooks/use-api'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'
import { NoBillingsYet } from '@/components/sales-trackers/no-billings-yet'
import { isNoBillingsYet } from '@/components/sales-trackers/billings-empty'
import { billingsFiscalMonths } from '@/lib/billings-fiscal'
import type { BillingsTarget, BillingsTargetCategory, BillingsTargetInput } from '@/types'

/**
 * Every target for the year, company and reps, on one page.
 *
 * Jason's third review: "Can we add a targets tab to allow me to set all Rep and Company
 * wide targets on one page". Before this, setting a year meant opening Overview for the
 * company figure and then each rep's tab in turn, which is eleven dialogs and no way to
 * see whether the parts add up to the whole.
 *
 *
 * ANNUAL FIGURES, NOT A WALL OF MONTHS
 *
 * A full month-level grid here would be (reps + 1) x 3 categories x 12 months, which is
 * around 400 input boxes on one screen. That is not a page anybody can check their work
 * on, and checking the work is the point: the reason to see everybody at once is to see
 * whether the rep targets add up to the company target.
 *
 * So this page is annual per subject per category, and the month detail stays where it
 * already worked, behind "Months" on each row. An annual figure typed here is spread
 * evenly across the twelve months, using the same `spreadAcrossMonths` the dialog uses,
 * so the parts still add back to the whole exactly rather than to a few pence short.
 *
 *
 * RESPREADING DESTROYS A SHAPE, SO IT SAYS SO FIRST
 *
 * A subject whose months were set individually - a seasonal ramp, or a rep who started in
 * August and has four months rather than twelve - is not recoverable once an even spread
 * has been written over it. Those rows are marked, and editing one warns before saving
 * rather than after. The alternative, silently flattening somebody's carefully staged
 * year because they corrected a typo in the annual box, is the kind of thing that is only
 * noticed a quarter later.
 *
 *
 * EMPTY IS NOT ZERO
 *
 * Clearing a box clears that subject's targets for the year, which is different from
 * setting them to zero: "no target" and "a target of nil" read differently in every
 * variance figure downstream, and the API models the distinction with null. Unchanged
 * rows send nothing at all.
 *
 *
 * ONE SAVE, ONE TRANSACTION
 *
 * The whole page saves in a single request, because a half-written year is worse than an
 * unwritten one - the months that did not land report as a shortfall against a target
 * nobody set. The server cap was raised from 500 to 2,000 rows for this, rather than
 * chunking the payload, since two requests cannot be one transaction.
 */

const CATEGORY_LABELS: Record<BillingsTargetCategory, string> = {
  total: 'Overall',
  software: 'Software',
  service: 'Service',
}

const CATEGORY_ORDER: BillingsTargetCategory[] = ['total', 'software', 'service']

const money = (n: number) => n.toLocaleString('en-CA', { maximumFractionDigits: 0 })

/** Identifies a subject: the company, or one rep. */
type Subject = {
  id: string
  name: string
  scope: 'company' | 'rep'
  repUserId: string | null
  repLabel: string | null
}

const cellKey = (subjectId: string, category: string) => `${subjectId}::${category}`

/** Which targets belong to a subject, from the flat list the API returns. */
function targetsFor(all: BillingsTarget[], subject: Subject) {
  return all.filter((t) =>
    subject.scope === 'company'
      ? t.scope === 'company'
      : t.scope === 'rep' &&
        (subject.repUserId ? t.rep_user_id === subject.repUserId : t.rep_label === subject.repLabel)
  )
}

/**
 * Whether a year was spread evenly or built month by month, and over how many months.
 *
 * `spreadAcrossMonths` puts the rounding remainder on the last month, so an evenly spread
 * year is eleven identical figures plus one that differs by pennies. Anything else - a
 * partial year, a ramp, a single month - is a shape somebody chose, and overwriting it
 * needs a warning.
 *
 * The count matters as much as the verdict. A subject with one month set still shows that
 * month's figure in the annual box, because the annual figure IS the sum of what is
 * stored; without being told it covers one month of twelve, 820,000 reads as a year.
 */
function shapeOf(values: Array<number | null>): {
  shape: 'none' | 'even' | 'shaped'
  monthsSet: number
} {
  const set = values.filter((v): v is number => v !== null)
  if (set.length === 0) return { shape: 'none', monthsSet: 0 }
  if (set.length < values.length) return { shape: 'shaped', monthsSet: set.length }
  const total = set.reduce((s, v) => s + v, 0)
  const even = spreadAcrossMonths(total, values.length)
  const isEven = values.every((v, i) => v !== null && Math.abs(v - (even[i] as number)) < 0.01)
  return { shape: isEven ? 'even' : 'shaped', monthsSet: set.length }
}

export default function SalesTrackersTargets() {
  useDocumentTitle('Sales targets')

  const [params, setParams] = useSearchParams()
  const fyParam = Number(params.get('fy'))
  const fy = Number.isInteger(fyParam) && fyParam > 0 ? fyParam : undefined
  const setFy = (next: number) => {
    const p = new URLSearchParams(params)
    p.set('fy', String(next))
    setParams(p, { replace: true })
  }

  const { toast } = useToast()
  const years = useBillingsFiscalYears()
  const effectiveFy = fy ?? years.data?.[0]?.fiscal_year
  const roster = useBillingsRepRoster(effectiveFy)
  const targets = useBillingsTargets(effectiveFy)
  const save = useSaveBillingsTargets()

  const [edits, setEdits] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [monthsFor, setMonthsFor] = useState<Subject | null>(null)

  // Unconditional, per the rules of hooks: the grid renders only once the data lands, and
  // the hook's callback ref is what makes measuring a late element work.
  const overflow = useOverflowFocus('Annual sales targets by rep and category')

  const months = useMemo(
    () => (effectiveFy ? billingsFiscalMonths(effectiveFy) : []),
    [effectiveFy]
  )

  const subjects: Subject[] = useMemo(() => {
    const reps = (roster.data ?? [])
      .filter((r) => r.can_hold_target)
      .map((r) => ({
        id: r.rep_user_id ?? r.rep_label,
        name: r.display,
        scope: 'rep' as const,
        repUserId: r.rep_user_id,
        repLabel: r.rep_user_id ? null : r.rep_label,
      }))
    return [
      { id: 'company', name: 'Company wide', scope: 'company', repUserId: null, repLabel: null },
      ...reps,
    ]
  }, [roster.data])

  /** Stored annual figure and month shape, per subject per category. */
  const stored = useMemo(() => {
    const all = targets.data?.targets ?? []
    const out: Record<
      string,
      { annual: number | null; shape: 'none' | 'even' | 'shaped'; monthsSet: number }
    > = {}
    for (const subject of subjects) {
      const mine = targetsFor(all, subject)
      for (const category of CATEGORY_ORDER) {
        const byMonth = new Map(
          mine.filter((t) => t.category === category).map((t) => [t.month_key, t.amount])
        )
        const values = months.map((m) => byMonth.get(m.key) ?? null)
        const set = values.filter((v): v is number => v !== null)
        out[cellKey(subject.id, category)] = {
          annual: set.length ? Math.round(set.reduce((s, v) => s + v, 0) * 100) / 100 : null,
          ...shapeOf(values),
        }
      }
    }
    return out
  }, [targets.data, subjects, months])

  const valueOf = (subjectId: string, category: string) => {
    const key = cellKey(subjectId, category)
    if (key in edits) return edits[key] as string
    const annual = stored[key]?.annual
    return annual === null || annual === undefined ? '' : String(annual)
  }

  const changed = (subjectId: string, category: string) => cellKey(subjectId, category) in edits

  /** Rows whose month shape would be flattened by saving the annual figure typed on them. */
  const wouldFlatten = Object.keys(edits).filter(
    (key) => stored[key]?.shape === 'shaped' && String(edits[key]).trim() !== ''
  )

  /** Totals across reps, so the parts can be checked against the company figure. */
  const repSum = (category: BillingsTargetCategory) =>
    subjects
      .filter((s) => s.scope === 'rep')
      .reduce((sum, s) => {
        const raw = valueOf(s.id, category)
        const n = Number(raw)
        return raw !== '' && Number.isFinite(n) ? sum + n : sum
      }, 0)

  const companyValue = (category: BillingsTargetCategory) => {
    const raw = valueOf('company', category)
    const n = Number(raw)
    return raw !== '' && Number.isFinite(n) ? n : null
  }

  const handleSave = async () => {
    setError('')
    const payload: BillingsTargetInput[] = []

    for (const subject of subjects) {
      for (const category of CATEGORY_ORDER) {
        const key = cellKey(subject.id, category)
        if (!(key in edits)) continue

        const raw = String(edits[key])
          .replace(/[^0-9.-]/g, '')
          .trim()
        const isBlank = raw === ''

        if (!isBlank && !Number.isFinite(Number(raw))) {
          setError(
            `${CATEGORY_LABELS[category]} for ${subject.name} is not a number. ` +
              `Fix it or clear the box.`
          )
          return
        }

        const base = {
          scope: subject.scope,
          rep_user_id: subject.scope === 'rep' ? subject.repUserId : null,
          rep_label: subject.scope === 'rep' && !subject.repUserId ? subject.repLabel : null,
          category,
        }

        if (isBlank) {
          // Clear only the months that hold something. Sending nulls for the rest would
          // be a delete of nothing, twelve times over, on every save.
          const mine = targetsFor(targets.data?.targets ?? [], subject).filter(
            (t) => t.category === category
          )
          for (const t of mine) payload.push({ ...base, month_key: t.month_key, amount: null })
          continue
        }

        const parts = spreadAcrossMonths(Number(raw), months.length)
        months.forEach((m, i) => {
          payload.push({ ...base, month_key: m.key, amount: parts[i] as number })
        })
      }
    }

    if (payload.length === 0) {
      setError('Nothing has changed, so there is nothing to save.')
      return
    }

    try {
      const result = await save.mutateAsync(payload)
      toast({
        title: 'Targets saved',
        description:
          result.cleared > 0
            ? `${result.saved} saved, ${result.cleared} cleared.`
            : `${result.saved} saved.`,
      })
      setEdits({})
    } catch (err) {
      setError(explainError(err, 'Could not save those targets.'))
    }
  }

  const dirty = Object.keys(edits).length > 0

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
            Sales targets
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Annual targets for the company and every rep. A figure typed here is spread evenly
            across the twelve months of the year. Amounts are CAD.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button variant="ghost" onClick={() => setEdits({})}>
              Discard changes
            </Button>
          )}
          <Button onClick={handleSave} disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save targets'}
          </Button>
        </div>
      </div>

      {years.data && years.data.length > 0 && (
        <FiscalYearPicker years={years.data} value={effectiveFy} onChange={setFy} />
      )}

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not save</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {targets.error &&
        (isNoBillingsYet(targets.error) ? (
          // Targets genuinely cannot be set yet: the fiscal years on offer are derived
          // from uploaded lines, so there is no year to hang a figure on until then.
          <NoBillingsYet what="Targets are set per fiscal year, and the years" />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not load the targets</AlertTitle>
            <AlertDescription>{explainError(targets.error)}</AlertDescription>
          </Alert>
        ))}

      {wouldFlatten.length > 0 && (
        <Alert>
          <AlertTitle>
            {wouldFlatten.length === 1
              ? 'One of these targets was set month by month'
              : `${wouldFlatten.length} of these targets were set month by month`}
          </AlertTitle>
          <AlertDescription>
            Saving replaces those months with an even split of the annual figure, so an uneven year
            or a part year will be flattened and cannot be recovered. Use &ldquo;Months&rdquo; on
            the row to keep the shape and change individual months instead.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Annual targets</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            An empty box means no target, which is not the same as a target of zero: a category
            nobody set reports nothing rather than a shortfall.
          </p>
        </CardHeader>
        <CardContent>
          {targets.isLoading || roster.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading targets…</p>
          ) : (
            <div className="overflow-x-auto" ref={overflow.ref} {...overflow.regionProps}>
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Annual sales targets for the company and each rep, by category, for the selected
                  fiscal year
                </caption>
                <thead>
                  <tr className="border-b">
                    <th scope="col" className="text-left font-medium py-2 pr-4">
                      Who
                    </th>
                    {CATEGORY_ORDER.map((c) => (
                      <th key={c} scope="col" className="text-right font-medium py-2 pr-4">
                        {CATEGORY_LABELS[c]}
                      </th>
                    ))}
                    <th scope="col" className="text-right font-medium py-2">
                      <span className="sr-only">Month level detail</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {subjects.map((subject) => (
                    <tr
                      key={subject.id}
                      className={`border-b last:border-0 ${
                        subject.scope === 'company' ? 'font-medium' : ''
                      }`}
                    >
                      <th scope="row" className="text-left font-normal py-2 pr-4">
                        {subject.name}
                      </th>
                      {CATEGORY_ORDER.map((category) => {
                        const key = cellKey(subject.id, category)
                        const cell = stored[key]
                        const shape = cell?.shape ?? 'none'
                        return (
                          <td key={category} className="py-2 pr-4 text-right">
                            <Input
                              type="text"
                              inputMode="decimal"
                              className={`h-8 w-32 text-right tabular-nums ml-auto ${
                                changed(subject.id, category) ? 'border-primary' : ''
                              }`}
                              value={valueOf(subject.id, category)}
                              onChange={(e) => setEdits((p) => ({ ...p, [key]: e.target.value }))}
                              // Twelve boxes on a row cannot share one label, so each
                              // names both its subject and its category.
                              aria-label={`${CATEGORY_LABELS[category]} annual target for ${subject.name}`}
                              aria-describedby={shape === 'shaped' ? `${key}-shape` : undefined}
                            />
                            {shape === 'shaped' && (
                              /* Stated in text, not by a colour or an icon: this is the
                                 difference between correcting a figure and destroying a
                                 year somebody staged deliberately. The count is what tells
                                 a reader the box above is a part year, not an annual one. */
                              <span
                                id={`${key}-shape`}
                                className="block text-xs text-muted-foreground mt-0.5"
                              >
                                {cell && cell.monthsSet < months.length
                                  ? `${cell.monthsSet} of ${months.length} months set`
                                  : 'set month by month'}
                              </span>
                            )}
                          </td>
                        )
                      })}
                      <td className="py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setMonthsFor(subject)}
                          aria-label={`Edit month by month targets for ${subject.name}`}
                        >
                          Months
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {/*
                   * The reason to see everybody on one page: whether the rep targets add
                   * up to the company one. Reported, never enforced - Jason's company
                   * figures deliberately differ from the sum of their parts, and the
                   * dialog already takes the same position on Overall against Software
                   * plus Service.
                   */}
                  <tr className="border-t-2">
                    <th scope="row" className="text-left font-medium py-2 pr-4">
                      Reps combined
                    </th>
                    {CATEGORY_ORDER.map((category) => {
                      const sum = repSum(category)
                      const company = companyValue(category)
                      const gap = company === null ? null : Math.round((sum - company) * 100) / 100
                      return (
                        <td key={category} className="py-2 pr-4 text-right tabular-nums">
                          {money(sum)}
                          {gap !== null && Math.abs(gap) >= 1 && (
                            <span className="block text-xs text-muted-foreground">
                              {gap > 0 ? '+' : '−'}
                              {money(Math.abs(gap))} against company
                            </span>
                          )}
                        </td>
                      )
                    })}
                    <td className="py-2" />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {monthsFor && (
        <TargetsDialog
          open
          onOpenChange={(open) => {
            if (!open) setMonthsFor(null)
          }}
          months={months}
          existing={targetsFor(targets.data?.targets ?? [], monthsFor)}
          scope={monthsFor.scope}
          repUserId={monthsFor.repUserId}
          repLabel={monthsFor.repLabel}
          subjectName={monthsFor.name}
        />
      )}
    </section>
  )
}
